/**
 * One-time migration: upload local IndexedDB data to Firestore
 * when a user signs in for the first time.
 */

import { collection, getDocs, writeBatch, doc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import * as localDb from '@/lib/db';

export interface MigrationResult {
  recipes: number;
  pantry: number;
  shopping: number;
  mealPlans: number;
  history: number;
  total: number;
  skipped: boolean; // true if user already had cloud data
}

const MAX_BATCH_OPS = 450; // Stay under Firestore's 500 limit

/** Strip `undefined` values (Firestore rejects them). */
function stripUndefined<T extends Record<string, unknown>>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

export async function migrateLocalToCloud(userId: string): Promise<MigrationResult> {
  const basePath = `users/${userId}`;

  // Check if user already has cloud data (any recipes = returning user)
  const existingRecipes = await getDocs(collection(db, `${basePath}/recipes`));
  if (!existingRecipes.empty) {
    return { recipes: 0, pantry: 0, shopping: 0, mealPlans: 0, history: 0, total: 0, skipped: true };
  }

  // Read all local data
  const [recipes, pantryItems, shoppingList, mealPlans, history, settings] = await Promise.all([
    localDb.getAllRecipes(),
    localDb.getPantryItems(),
    localDb.getShoppingList(),
    localDb.getMealPlanEntries(),
    localDb.getCookingHistory(),
    localDb.getSettings(),
  ]);

  const totalItems =
    recipes.length + pantryItems.length + shoppingList.length + mealPlans.length + history.length;

  if (totalItems === 0) {
    return { recipes: 0, pantry: 0, shopping: 0, mealPlans: 0, history: 0, total: 0, skipped: false };
  }

  // Build write operations
  type WriteOp = { ref: ReturnType<typeof doc>; data: Record<string, unknown> };
  const ops: WriteOp[] = [];

  for (const recipe of recipes) {
    ops.push({ ref: doc(db, `${basePath}/recipes`, recipe.id), data: recipe as unknown as Record<string, unknown> });
  }
  for (const item of pantryItems) {
    ops.push({ ref: doc(db, `${basePath}/pantryItems`, item.id), data: item as unknown as Record<string, unknown> });
  }
  for (const item of shoppingList) {
    ops.push({ ref: doc(db, `${basePath}/shoppingList`, item.id), data: item as unknown as Record<string, unknown> });
  }
  for (const entry of mealPlans) {
    ops.push({ ref: doc(db, `${basePath}/mealPlans`, entry.id), data: entry as unknown as Record<string, unknown> });
  }
  for (const entry of history) {
    ops.push({ ref: doc(db, `${basePath}/cookingHistory`, entry.id), data: entry as unknown as Record<string, unknown> });
  }

  // Upload settings
  if (settings) {
    ops.push({
      ref: doc(db, `${basePath}/meta/settings`),
      data: settings as unknown as Record<string, unknown>,
    });
  }

  // Commit in batches (max ~450 ops per batch to stay safe)
  for (let i = 0; i < ops.length; i += MAX_BATCH_OPS) {
    const chunk = ops.slice(i, i + MAX_BATCH_OPS);
    const batch = writeBatch(db);
    for (const op of chunk) {
      batch.set(op.ref, stripUndefined(op.data));
    }
    await batch.commit();
  }

  return {
    recipes: recipes.length,
    pantry: pantryItems.length,
    shopping: shoppingList.length,
    mealPlans: mealPlans.length,
    history: history.length,
    total: totalItems,
    skipped: false,
  };
}
